import type { AgentSessionEvent, AgentSessionViewModel } from '@inf-monkeys-tech/monkeys';
import {
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';
import {
  AgentWorkbenchActivity,
  type AgentWorkbenchArtifactIntent,
} from './AgentWorkbenchActivity';
import type { AgentWorkbenchApprovalIntent } from './AgentWorkbenchApproval';
import { AgentWorkbenchMessage, AgentWorkbenchMessageContent } from './AgentWorkbenchMessage';

type MessageEvent = Extract<AgentSessionEvent, { eventType: 'message' }>;

export interface AgentWorkbenchThreadMessage {
  id: string;
  role: MessageEvent['payload']['role'];
  parts: MessageEvent['payload']['parts'];
  sequence: number;
}

type ThreadEntry =
  | { type: 'message'; sequence: number; message: AgentWorkbenchThreadMessage }
  | { type: 'error'; sequence: number; event: Extract<AgentSessionEvent, { eventType: 'error' }> }
  | { type: 'activity'; sequence: number; event: AgentSessionEvent };

export interface AgentWorkbenchThreadClassNames {
  root?: string;
  list?: string;
  message?: string;
  messageContent?: string;
  activity?: string;
  empty?: string;
  loading?: string;
}

export interface AgentWorkbenchThreadProps {
  viewModel?: AgentSessionViewModel;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  autoScroll?: boolean;
  className?: string;
  classNames?: AgentWorkbenchThreadClassNames;
  renderMessageContent?: (message: AgentWorkbenchThreadMessage) => ReactNode;
  onApprovalDecision?: (intent: AgentWorkbenchApprovalIntent) => void;
  onArtifactOpen?: (intent: AgentWorkbenchArtifactIntent) => void;
}

function materializeMessages(events: readonly AgentSessionEvent[]): AgentWorkbenchThreadMessage[] {
  const messages = new Map<string, AgentWorkbenchThreadMessage>();
  for (const event of events) {
    if (event.eventType !== 'message') continue;
    const current = messages.get(event.payload.messageId);
    const parts = event.payload.operation === 'replace'
      ? event.payload.parts
      : [...(current?.parts ?? []), ...event.payload.parts];
    messages.set(event.payload.messageId, {
      id: event.payload.messageId,
      role: event.payload.role,
      parts,
      sequence: event.sequence,
    });
  }
  return [...messages.values()];
}

function logicalActivityKey(event: AgentSessionEvent): string | undefined {
  switch (event.eventType) {
    case 'reasoning': return `reasoning:${event.payload.reasoningId}`;
    case 'plan': return `plan:${event.payload.planId}`;
    case 'task': return `task:${event.payload.taskId}`;
    case 'tool': return `tool:${event.payload.toolCallId}`;
    case 'approval': return `approval:${event.payload.approvalId}`;
    case 'artifact': return `artifact:${event.payload.artifactId}`;
    case 'usage': return 'usage';
    case 'status': return 'status';
    default: return undefined;
  }
}

function createThreadEntries(viewModel: AgentSessionViewModel): ThreadEntry[] {
  const messages = materializeMessages(viewModel.events).map<ThreadEntry>((message) => ({
    type: 'message',
    sequence: message.sequence,
    message,
  }));
  const activity = new Map<string, AgentSessionEvent>();
  for (const event of viewModel.events) {
    const key = logicalActivityKey(event);
    if (key) activity.set(key, event);
  }
  return [
    ...messages,
    ...viewModel.events
      .filter((event): event is Extract<AgentSessionEvent, { eventType: 'error' }> => event.eventType === 'error')
      .map<ThreadEntry>((event) => ({ type: 'error', sequence: event.sequence, event })),
    ...[...activity.values()].map<ThreadEntry>((event) => ({
      type: 'activity',
      sequence: event.sequence,
      event,
    })),
  ].sort((left, right) => left.sequence - right.sequence);
}

function partText(part: unknown): string {
  if (typeof part === 'string') return part;
  if (!part || typeof part !== 'object' || Array.isArray(part)) return String(part ?? '');
  const record = part as Record<string, unknown>;
  if (typeof record.text === 'string') return record.text;
  if (typeof record.name === 'string') return record.name;
  return JSON.stringify(record, null, 2);
}

function DefaultMessageContent({ message }: { message: AgentWorkbenchThreadMessage }) {
  const content = message.parts.map(partText).filter(Boolean).join('');
  return <div className="whitespace-pre-wrap break-words">{content}</div>;
}

function singleEventViewModel(
  viewModel: AgentSessionViewModel,
  event: AgentSessionEvent,
): AgentSessionViewModel {
  return {
    ...viewModel,
    events: [event],
    lastSequence: event.sequence,
  };
}

export function AgentWorkbenchThread({
  viewModel,
  loading = false,
  emptyTitle,
  emptyDescription,
  autoScroll = true,
  className,
  classNames,
  renderMessageContent,
  onApprovalDecision,
  onArtifactOpen,
}: AgentWorkbenchThreadProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench;
  const scrollRef = useRef<HTMLDivElement>(null);
  const entries = useMemo(
    () => viewModel ? createThreadEntries(viewModel) : [],
    [viewModel],
  );

  useEffect(() => {
    if (!autoScroll || !viewModel) return;
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [autoScroll, viewModel?.lastSequence, viewModel?.sessionId]);

  return (
    <div
      ref={scrollRef}
      role="log"
      aria-label={messages.label}
      className={cn('min-h-0 flex-1 overflow-y-auto bg-background', classNames?.root, className)}
    >
      {loading && entries.length === 0 ? (
        <div className={cn('flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground', classNames?.loading)} role="status">
          <span className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" aria-hidden="true" />
          <span>{messages.navigation.loading}</span>
        </div>
      ) : entries.length === 0 ? (
        <div className={cn('flex min-h-48 flex-col items-center justify-center px-6 text-center', classNames?.empty)}>
          {emptyTitle ? <h2 className="text-base font-semibold text-foreground">{emptyTitle}</h2> : null}
          {emptyDescription ? <p className="mt-1 max-w-lg text-sm text-muted-foreground">{emptyDescription}</p> : null}
        </div>
      ) : (
        <div className={cn('mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6', classNames?.list)}>
          {entries.map((entry) => {
            if (entry.type === 'error') {
              return (
                <div
                  key={`error:${entry.event.eventId}`}
                  role="alert"
                  className={cn('rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-text', classNames?.activity)}
                >
                  {entry.event.payload.message}
                </div>
              );
            }
            if (entry.type === 'activity') {
              return (
                <AgentWorkbenchActivity
                  key={`activity:${entry.event.eventId}`}
                  viewModel={singleEventViewModel(viewModel!, entry.event)}
                  ariaLabel={`${messages.label} ${entry.sequence + 1}`}
                  className={classNames?.activity}
                  onApprovalDecision={onApprovalDecision}
                  onArtifactOpen={onArtifactOpen}
                />
              );
            }
            const { message } = entry;
            const isUser = message.role === 'user';
            const isSystem = message.role === 'system';
            if (isSystem) {
              return (
                <div key={message.id} className={cn('text-center text-xs text-muted-foreground', classNames?.message)}>
                  {renderMessageContent?.(message) ?? <DefaultMessageContent message={message} />}
                </div>
              );
            }
            return (
              <AgentWorkbenchMessage
                key={message.id}
                role={message.role}
                className={cn(
                  'flex-row items-start gap-3',
                  isUser && 'flex-row-reverse',
                  classNames?.message,
                )}
                data-message-role={message.role}
              >
                <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full', isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground')} aria-hidden="true">
                  <span className="size-2 rounded-full bg-current" />
                </span>
                <AgentWorkbenchMessageContent
                  className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-6',
                    !isUser && 'bg-card text-card-foreground',
                    classNames?.messageContent,
                  )}
                >
                  {renderMessageContent?.(message) ?? <DefaultMessageContent message={message} />}
                </AgentWorkbenchMessageContent>
              </AgentWorkbenchMessage>
            );
          })}
        </div>
      )}
    </div>
  );
}
